import React, { useMemo } from 'react'
import { useGetDisplayCreativeMetrics } from '../../../api/dealership/campaigns/reports/useGetDisplayCreativeMetrics'
import { DisplayCreative } from '../../../lib/types/DisplayCreativeMetrics'
import { Pill } from '@demandlocal/components'
import HoverMedia from '../../HoverMedia'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useParams } from 'react-router'
import { DateRange } from '../../../lib/types/DateRange'
import { match } from 'ts-pattern'

type Props = {
  creativeType: 'image' | 'video'
  filters: DateRange
}

const TopPerformingCreatives = ({ filters, creativeType }: Props): JSX.Element => {
  const { campaign_id } = useParams()
  const { data: creativeMetrics } = useGetDisplayCreativeMetrics({
    filters: {
      campaign_id: campaign_id,
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const highestVtrCreative = useMemo(() => {
    if (!creativeMetrics?.data || !Array.isArray(creativeMetrics.data)) return null

    const creative = match(creativeType)
      .with('video', () => {
        return creativeMetrics?.data
          .sort((a: DisplayCreative, b: DisplayCreative) => (a.vtr < b.vtr ? 1 : -1))
          .filter(({ image_url }: { image_url?: string }) => image_url)[0]
      })
      .with('image', () => {
        return creativeMetrics?.data
          .sort((a: DisplayCreative, b: DisplayCreative) => (a.ctr < b.ctr ? 1 : -1))
          .filter(({ image_url }: { image_url?: string }) => image_url)[0]
      })
      .exhaustive()
    if (creative?.image_url?.includes('http:'))
      creative.image_url = creative.image_url.replace('http', 'https')
    return creative
  }, [creativeMetrics?.data, creativeType])

  const mostImpressionCreative = useMemo(() => {
    if (!creativeMetrics?.data || !Array.isArray(creativeMetrics.data)) return null

    const creative = creativeMetrics?.data
      ?.sort((a: DisplayCreative, b: DisplayCreative) =>
        a.impressions < b.impressions ? 1 : -1,
      )
      .filter(({ image_url }: { image_url?: string }) => image_url)[0]

    if (creativeType === 'video' && creative?.image_url?.includes('http:'))
      creative.image_url = creative.image_url.replace('http', 'https')

    return creative
  }, [creativeMetrics?.data, creativeType])

  const mostViewedCreative = useMemo(() => {
    if (!creativeMetrics?.data || !Array.isArray(creativeMetrics.data)) return null

    const creative = match(creativeType)
      .with('video', () => {
        return creativeMetrics?.data
          ?.sort((a: DisplayCreative, b: DisplayCreative) => (a.views < b.views ? 1 : -1))
          .filter(({ image_url }: { image_url?: string }) => image_url)[0]
      })
      .with('image', () => {
        return creativeMetrics?.data
          .sort((a: DisplayCreative, b: DisplayCreative) =>
            a.clicks < b.clicks ? 1 : -1,
          )
          .filter(({ image_url }: { image_url?: string }) => image_url)[0]
      })
      .exhaustive()

    if (creative?.image_url?.includes('http:'))
      creative.image_url = creative.image_url.replace('http', 'https')
    return creative
  }, [creativeMetrics?.data, creativeType])

  return (
    <>
      {highestVtrCreative?.image_url &&
        mostImpressionCreative?.image_url &&
        mostViewedCreative?.image_url && (
          <div className={'flex grid grid-cols-3 gap-5'}>
            <div className={'p-5 min-h-[300px] border-2 rounded-xl flex flex-col'}>
              <div
                className={
                  'pb-4 flex justify-between font-bold text-md text-primary-xiketic'
                }>
                <span>Highest {creativeType === 'video' ? 'VTR' : 'CTR'}</span>
                <span>
                  <Pill type={'success'}>
                    {/* eslint-disable-next-line no-constant-binary-expression */}
                    {Math.round(
                      (creativeType === 'video'
                        ? highestVtrCreative?.vtr
                        : highestVtrCreative?.ctr) * 1000,
                    ) / 10}{' '}
                    %
                  </Pill>
                </span>
              </div>
              <div className={'flex min-h-[200px] items-center justify-center'}>
                {
                  <HoverMedia
                    src={highestVtrCreative.image_url}
                    type={creativeType}
                    className={`rounded-xl w-fit h-fit object-cover object-scale-down max-h-40`}
                  />
                }
              </div>
              <div
                className={
                  'pt-5 pb-2 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                {highestVtrCreative.creative_name}
              </div>
              <div className={'flex justify-between text-xs text-primary-xiketic'}>
                <span>Ad Size: {highestVtrCreative.creative_size}</span>
              </div>
            </div>
            <div className={'p-5 min-h-[300px] border-2 rounded-xl'}>
              <div
                className={
                  'pb-4 flex justify-between font-bold text-md text-primary-xiketic'
                }>
                <span>Most Impressions</span>
                <span>
                  <Pill type={'success'}>
                    {formatNumber(mostImpressionCreative.impressions) ?? 0}
                  </Pill>
                </span>
              </div>
              <div className={'flex min-h-[200px] items-center justify-center'}>
                <HoverMedia
                  src={mostImpressionCreative.image_url}
                  type={creativeType}
                  className={`rounded-xl w-fit h-fit object-cover object-scale-down max-h-40`}
                />
              </div>
              <div
                className={
                  'pt-5 pb-2 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                {mostImpressionCreative.creative_name}
              </div>
              <div className={'flex justify-between text-xs text-primary-xiketic'}>
                <span>Ad Size: {mostImpressionCreative.creative_size}</span>
              </div>
            </div>
            <div className={'p-5 min-h-[300px] border-2 rounded-xl'}>
              <div
                className={
                  'pb-4 flex justify-between font-bold text-md text-primary-xiketic'
                }>
                <span>
                  Most {creativeType === 'video' ? 'Viewed Through' : 'Clicked'}
                </span>
                <span>
                  <Pill type={'success'}>
                    {formatNumber(
                      creativeType === 'video'
                        ? mostViewedCreative.views
                        : mostViewedCreative.clicks,
                    ) ?? 0}
                  </Pill>
                </span>
              </div>

              <div className={'flex min-h-[200px] items-center justify-center'}>
                {
                  <HoverMedia
                    src={mostViewedCreative.image_url}
                    type={creativeType}
                    className={`rounded-xl w-fit h-fit object-cover object-scale-down max-h-40`}
                  />
                }
              </div>
              <div
                className={
                  'pt-5 pb-2 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                {mostViewedCreative.creative_name}
              </div>
              <div className={' flex justify-between text-xs text-primary-xiketic'}>
                <span>Ad Size: {mostViewedCreative.creative_size}</span>
              </div>
            </div>
          </div>
        )}
    </>
  )
}

export default TopPerformingCreatives
